/**
 * PropertyPicker — выбор Объекта в модале Работы.
 *
 * Было: нативный <select> со всеми Объектами орги. При 20+ Объектах это
 * длинная простыня без поиска, листать приходится глазами.
 * Стало: тот же приём, что в AssigneePicker — поле + панель с поиском и
 * списком, который сужается по мере ввода (название или адрес). На десктопе
 * панель раскрывается под полем, на телефоне — шторка снизу.
 *
 * Выбор одиночный, поэтому «Готово» не нужно: тап по Объекту выбирает его и
 * закрывает панель. Enter в поиске выбирает первый найденный.
 *
 * Список приходит снаружи (properties) — модал уже грузит его сам и
 * дописывает в него только что созданный Объект.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { track } from '../analytics';
import useIsNarrow from '../hooks/useIsNarrow';
import useEscape from '../hooks/useEscape';
import { focusQuiet, revealRow } from '../utils/scroll';
import { backdropClose } from '../utils/modalBackdrop';
import { normalizeQuery } from '../utils/teamGrouping';
import Icon from './Icon';

const FIELD = { height: 36, minHeight: 36, maxHeight: 36, fontSize: 13, paddingTop: 0, paddingBottom: 0 };
// Больше строк в DOM не рисуем — дальше человек сужает поиск
const RENDER_LIMIT = 80;

/** Строка Объекта в панели. Верхнеуровневый компонент, не вложенный. */
function PropertyRow({ p, selected, onPick }) {
  return (
    <button type="button" onClick={onPick}
      // Без фокуса по мыши: браузер подкручивал модалку к сфокусированной строке
      onMouseDown={(e) => e.preventDefault()}
      aria-pressed={selected}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
        padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border)',
        background: selected ? 'rgba(79,142,247,0.08)' : 'transparent', cursor: 'pointer', color: 'var(--text)',
      }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {p.name}
        </span>
        {p.address && (
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {p.address}
          </span>
        )}
      </span>
      {selected && <Icon name="check" size={16} style={{ color: 'var(--accent)', flexShrink: 0 }} />}
    </button>
  );
}

export default function PropertyPicker({ value = '', onChange, properties = [] }) {
  const { t } = useTranslation();
  const narrow = useIsNarrow();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const panelRef = useRef(null);

  useEscape(open, () => setOpen(false));
  useEffect(() => {
    // На телефоне клавиатура закрыла бы половину шторки — фокус только на десктопе
    if (!open || narrow) return undefined;
    focusQuiet(searchRef.current);
    const id = requestAnimationFrame(() => revealRow(panelRef.current));
    return () => cancelAnimationFrame(id);
  }, [open, narrow]);

  // Десктоп: тап мимо поля и панели закрывает её. На телефоне это делает фон шторки.
  useEffect(() => {
    if (!open || narrow) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open, narrow]);

  const current = useMemo(
    () => properties.find((p) => String(p.id) === String(value)) || null,
    [properties, value]
  );

  const q = normalizeQuery(query);
  // Регистронезависимый матч — в JS: SQLite/LOWER не знает кириллицу, а тут это и не нужно
  const hits = useMemo(() => (q
    ? properties.filter((p) => [p.name, p.address].some((v) => v && String(v).toLocaleLowerCase().includes(q)))
    : properties), [properties, q]);
  const shown = hits.slice(0, RENDER_LIMIT);

  const openPanel = () => {
    setOpen(true);
    setQuery('');
    track('property_picker_opened', { available: properties.length, has_value: !!current });
  };

  const pick = (p, source) => {
    setOpen(false);
    if (String(p.id) !== String(value)) onChange(String(p.id));
    track('property_picked', { source, query_length: q.length, available: properties.length });
  };

  const field = (
    <button type="button" data-ph-capture="property_picker_open"
      onClick={() => (open ? setOpen(false) : openPanel())}
      aria-expanded={open} aria-haspopup="listbox"
      className="form-select"
      style={{
        ...FIELD, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        width: '100%', textAlign: 'left', cursor: 'pointer', boxSizing: 'border-box',
        color: current ? 'var(--text)' : 'var(--text3)',
      }}>
      <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {current ? current.name : t('tasks.selectProperty')}
      </span>
      <Icon name="chevronDown" size={14} style={{ transform: open ? 'rotate(180deg)' : 'none' }} />
    </button>
  );

  let body;
  if (!properties.length) {
    body = <div style={{ padding: 16, fontSize: 13, color: 'var(--text2)' }}>{t('propertyPicker.noProperties')}</div>;
  } else if (!hits.length) {
    body = <div style={{ padding: 16, fontSize: 13, color: 'var(--text2)' }}>{t('propertyPicker.searchEmpty')}</div>;
  } else {
    body = (
      <>
        {shown.map((p) => (
          <PropertyRow key={p.id} p={p} selected={String(p.id) === String(value)}
            onPick={() => pick(p, q ? 'search' : 'list')} />
        ))}
        {hits.length > shown.length && (
          <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text3)' }}>
            {t('propertyPicker.refine', { shown: shown.length, total: hits.length })}
          </div>
        )}
      </>
    );
  }

  const panelInner = (
    <>
      <div className="search-box" style={{ margin: 0, padding: 8, borderBottom: '1px solid var(--border)' }}>
        <span className="search-icon" style={{ left: 18 }}>🔍</span>
        <input ref={searchRef} type="search" className="form-input" value={query}
          onChange={(e) => setQuery(e.target.value)}
          // Панель живёт внутри <form> Работы: Enter не должен её отправлять,
          // вместо этого выбирает первый найденный Объект
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            if (shown.length) pick(shown[0], 'enter');
          }}
          onBlur={() => { if (query.trim()) track('property_picker_search', { length: query.trim().length, results: hits.length }); }}
          placeholder={t('propertyPicker.searchPlaceholder')}
          autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
          data-form-type="other" data-lpignore="true"
          style={{ ...FIELD, WebkitAppearance: 'none' }} />
      </div>
      <div role="listbox" style={{ overflowY: 'auto', flex: 1, minHeight: 0, maxHeight: narrow ? 'none' : 300 }}>{body}</div>
    </>
  );

  return (
    <div ref={rootRef}>
      {field}
      {open && !narrow && (
        <div ref={panelRef} style={{
          marginTop: 6, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
        }}>
          {panelInner}
        </div>
      )}
      {open && narrow && createPortal(
        <div {...backdropClose(() => setOpen(false))} style={{
          position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'flex-end',
        }}>
          <div role="dialog" aria-label={t('propertyPicker.sheetTitle')} style={{
            width: '100%', maxHeight: '82dvh', background: 'var(--bg)', borderRadius: '14px 14px 0 0',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px 4px' }}>
              <span style={{ fontWeight: 600, fontSize: 15 }}>{t('propertyPicker.sheetTitle')}</span>
              <button type="button" className="btn btn-ghost btn-sm" aria-label={t('common.close')} onClick={() => setOpen(false)}>
                <Icon name="x" size={16} />
              </button>
            </div>
            {panelInner}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
